import React, { useState, useCallback } from "react";
import {
  Box,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AmSatelliteImageryLegend from "./AmSatelliteImageryLegend";
import AmSatelliteTilePlotMap from "./AmSatelliteTilePlotMap";
import useMapLoading from "../../hooks/useMapLoading";
import AmAdvancedMaps from "./AmAdvancedMaps";
import AgroPallete1 from "../../assets/images/AgroPalette1.png";

// Tab Panel component
const TabPanel = ({ children, value, index }) => {
  return (
    <div hidden={value !== index} id={`map-tabpanel-${index}`}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const AmSatelliteMapOverlay = ({ tile, polygon }) => {
  const [activeTile, setActiveTile] = useState("ndvi");
  const [activeTab, setActiveTab] = useState(0);
  const { isLoading, setIsLoading, MapEventsHandler } = useMapLoading();
  const { t } = useTranslation();

  const handleTileChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      if (newValue !== "none") {
        setIsLoading(true);
      }
      setActiveTile(newValue);
    },
    [setIsLoading]
  );

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box
      sx={{
        width: "100%",
        position: "relative",
        height: "auto",
      }}
      fullWidth
    >
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ width: "100%" }}
        >
          <Tab label="Basic" />
          <Tab label="Advanced" />
        </Tabs>
      </Box>

      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            padding: "20px",
            borderRadius: "8px",
          }}
        >
          <CircularProgress size={40} />
          <Typography variant="body2" mt={1}>
            {`${t("Loading satellite imagery")}...`}
          </Typography>
        </Box>
      )}

      <TabPanel value={activeTab} index={0}>
        <FormControl
          sx={{
            mb: 1,
            zIndex: 1001,
            borderRadius: 1,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            position: "relative",
            top: "auto",
            left: "auto",
          }}
          fullWidth
        >
          <RadioGroup
            row
            value={activeTile}
            onChange={handleTileChange}
            sx={{ px: 2 }}
          >
            <FormControlLabel
              value="ndvi"
              control={<Radio size="small" />}
              label="Crop Health"
            />
            <FormControlLabel
              value="ndwi"
              control={<Radio size="small" />}
              label="Soil Moisture"
            />
            <FormControlLabel
              value="none"
              control={<Radio size="small" />}
              label="Off"
            />
          </RadioGroup>
        </FormControl>
        <AmSatelliteTilePlotMap
          tile={tile}
          polygon={polygon}
          activeTile={activeTile}
          MapEventsHandler={MapEventsHandler}
          setIsLoading={setIsLoading}
        />
        <AmSatelliteImageryLegend activeTile={activeTile} />
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        <Paper sx={{ px: 2, pb: 2 }}>
          <AmAdvancedMaps tile={tile} polygon={polygon} />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Divider sx={{ my: 2, width: "100%" }} />
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, textAlign: "center", color: "text.secondary" }}
            >
              {t(
                "Index Scale: Represents intensity — 0 indicates lower values, 1 indicates higher values."
              )}
            </Typography>
            <img
              src={AgroPallete1}
              alt="AgroPalette"
              style={{
                width: "90%",
                maxWidth: 350,
                marginTop: 8,
                borderRadius: 8,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            />
          </Box>
        </Paper>
      </TabPanel>
    </Box>
  );
};

export default React.memo(AmSatelliteMapOverlay);
